/**
 * offlineGuard.ts — Intercepta escritas no Drive quando offline
 *
 * Fluxo:
 *   online  → chama driveSaveFn() direto → { queued: false }
 *   offline → enqueue no IndexedDB → atualiza syncStore → { queued: true }
 *   online mas Drive falha com erro de rede → tenta enqueue como fallback
 *   Drive falha com 412 → propaga o erro (conflito, não enfileira)
 *   IndexedDB indisponível → propaga o erro original (comportamento atual)
 */

import { enqueue, getQueueCount, initOfflineQueue } from './index';
import { DriveConflictError } from '../googleDrive/driveFetch';
import { logger } from '../../utils/logger';
import type { OfflineOperationType } from './types';
import { OfflineQueueFullError } from './types';

export interface OfflineGuardResult {
  queued: boolean;
}

export interface OfflineGuardOptions {
  baseEtag?: string;
  baseSnapshot?: string;
  operationType?: OfflineOperationType;
}

/**
 * Determina se um erro é de rede (fetch falhando, sem resposta).
 * TypeError é lançado pelo fetch() quando não há conectividade.
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return msg.includes('failed to fetch') ||
           msg.includes('network') ||
           msg.includes('net::') ||
           msg.includes('load failed');
  }
  return false;
}

/**
 * Intercepta uma escrita no Drive com proteção offline.
 *
 * Se online: tenta salvar normalmente. Se offline ou erro de rede: enfileira no IndexedDB.
 * O store chamador mantém o optimistic update (dados seguros no IDB).
 *
 * @param fileKey - Identificador do arquivo no Drive (ex: 'referencias.json')
 * @param payload - JSON stringified do estado completo
 * @param driveSaveFn - Função que efetivamente salva no Drive
 * @param options - ETag, snapshot, tipo de operação
 * @returns { queued: true } se enfileirou, { queued: false } se salvou normalmente
 */
export async function withOfflineGuard(
  fileKey: string,
  payload: string,
  driveSaveFn: () => Promise<void>,
  options?: OfflineGuardOptions
): Promise<OfflineGuardResult> {
  const operationType = options?.operationType ?? 'overwrite';

  // Se offline, enfileirar diretamente
  if (!navigator.onLine) {
    return tryEnqueue(fileKey, payload, operationType, options);
  }

  // Online — tentar salvar normalmente
  try {
    await driveSaveFn();
    return { queued: false };
  } catch (error) {
    // 412 Conflict → propagar (não enfileirar — precisa de resolução)
    if (error instanceof DriveConflictError) {
      throw error;
    }

    // OfflineQueueFullError → propagar
    if (error instanceof OfflineQueueFullError) {
      throw error;
    }

    // Erro de rede → tentar enfileirar como fallback
    if (isNetworkError(error)) {
      logger.info('offlineGuard', `Erro de rede ao salvar ${fileKey}. Enfileirando offline.`);
      try {
        return await tryEnqueue(fileKey, payload, operationType, options);
      } catch (enqueueError) {
        // IDB também falhou — propagar erro original
        logger.error('offlineGuard', `Falha no enqueue fallback para ${fileKey}:`, enqueueError);
        throw error;
      }
    }

    // Outro erro (400, 403, etc.) → propagar normalmente
    throw error;
  }
}

/**
 * Tenta enfileirar operação no IndexedDB e atualizar syncStore.
 */
async function tryEnqueue(
  fileKey: string,
  payload: string,
  operationType: OfflineOperationType,
  options?: OfflineGuardOptions
): Promise<OfflineGuardResult> {
  // Garantir que o IDB está inicializado
  const ok = await initOfflineQueue();
  if (!ok) {
    throw new Error(`IndexedDB não disponível para enfileirar ${fileKey}`);
  }

  await enqueue({
    fileKey,
    operationType,
    payload,
    baseEtag: options?.baseEtag,
    baseSnapshot: options?.baseSnapshot,
  });

  // Atualizar syncStore com a contagem real
  try {
    const { useSyncStore } = await import('../../stores/syncStore');
    const count = await getQueueCount();
    useSyncStore.setState({ queuedCount: count });
    if (!navigator.onLine) {
      useSyncStore.setState({ status: 'queued' });
    }
  } catch {
    // syncStore import falhou — não crítico
  }

  logger.info('offlineGuard', `Operação enfileirada para ${fileKey} (${operationType})`);
  return { queued: true };
}
